import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UseESPNAthletesProps {
  sport?: string;
  searchTerm?: string;
  team?: string;
  position?: string;
  claimStatus?: string;
  isCollegiate?: boolean;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

export interface ESPNAthlete {
  id: string;
  espn_id: number;
  sport: string;
  league: string | null;
  name: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  short_name: string | null;
  age: number | null;
  birth_date: string | null;
  height: string | null;
  weight: string | null;
  birthplace_city: string | null;
  birthplace_state: string | null;
  birthplace_country: string | null;
  college: string | null;
  draft_year: number | null;
  draft_round: number | null;
  draft_number: number | null;
  years_experience: number | null;
  current_team_id: number | null;
  current_team_name: string | null;
  current_team_logo: string | null;
  current_team_abbreviation: string | null;
  position: string | null;
  jersey_number: number | null;
  headshot_url: string | null;
  bio: string | null;
  career_stats: Record<string, any> | null;
  current_season_stats: Record<string, any> | null;
  claim_status: string | null;
  claimed_by_profile_id: string | null;
  claimed_at: string | null;
  is_verified: boolean | null;
  last_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  api_data: Record<string, any> | null;
  is_collegiate: boolean | null;
  conference: string | null;
}

interface ESPNAthletesResponse {
  athletes: ESPNAthlete[];
  total: number;
  limit: number;
  offset: number;
  stats?: {
    verified: number;
    claimed: number;
    unclaimed: number;
  };
}

export const useESPNAthletes = ({
  sport,
  searchTerm,
  team,
  position,
  claimStatus,
  isCollegiate,
  limit = 50,
  offset = 0,
  enabled = true,
}: UseESPNAthletesProps = {}) => {
  return useQuery<ESPNAthletesResponse>({
    queryKey: ['espn-athletes', sport, searchTerm, team, position, claimStatus, isCollegiate, limit, offset],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('search-espn-athletes', {
        body: {
          sport,
          searchTerm,
          team,
          position,
          claimStatus,
          isCollegiate,
          limit,
          offset,
        },
      });

      if (error) {
        throw error;
      }

      return data as ESPNAthletesResponse;
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
